// EWM-327 — the CT `productBrand` attribute (and the Algolia `brand` field on
// sd* indices) arrives in three shapes, verified live on electus-dev 2026-06-04
// across 8,924 products:
//   - plain string            e.g. "BRASS MONKEY" / "Portasol"   (~30%)
//   - JSON-envelope STRING     '{"brandInfo":{"name":"PORTASOL",...}}'  (~8%)
//   - empty string / absent    (~62%)
// The envelope is always a JSON *string*, never a pre-parsed object — EWM-194
// stripped the parse after sampling only plain-string SKUs, so the ~8% with
// the envelope rendered the raw blob (Algolia) or no brand at all (PDP). This
// helper normalizes every shape to the display name, treating empty / missing /
// malformed as absent so the brand row stays hidden (no regression vs EWM-194).
//
// The envelope also carries logo data (url, altText, dimensions); rendering the
// brand logo is a separate enhancement and intentionally out of scope here.

const fromBrandInfo = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const info = (value as { brandInfo?: unknown }).brandInfo;
  if (!info || typeof info !== 'object') return undefined;
  const name = (info as { name?: unknown }).name;
  if (typeof name !== 'string') return undefined;
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const normalizeBrandName = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return undefined;
    if (trimmed.startsWith('{')) {
      try {
        return fromBrandInfo(JSON.parse(trimmed));
      } catch {
        return undefined;
      }
    }
    return trimmed;
  }
  // Legacy Algolia `string[]` shape — normalize the first element (which may
  // itself be a plain string or a JSON envelope).
  if (Array.isArray(raw)) {
    return raw.length > 0 ? normalizeBrandName(raw[0]) : undefined;
  }
  // Defensive: an already-parsed `{ brandInfo: { name } }` object.
  return fromBrandInfo(raw);
};
